import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";

import { DailyMessage } from "models/DailyMessage";
import { ReservationBrowsingPeriod, Mode } from "models/Mode";
import { Reservation, TimeRange } from "models/Reservation";
import { TableStatus, TableState, Table } from "models/Table";

import { getDateFormatted } from "../helpers/date/getDateFormatted";
import { getReservationsSortedByStartDate } from "../helpers/reservations";
import * as dailyMessageRepository from "../repositories/dailyMessage";
import * as tablesRepository from "../repositories/tables";

export interface UseTablesInterface {
    browsingPeriod: ReservationBrowsingPeriod
    mode: Mode,
    onModeChange: (mode: Mode) => void,
    previousMode?: Mode,
    queryData: TimeRange,
    reservation?: Reservation,
    reservationId?: string
    selectedStartDate: string
}

export interface UseTablesReturn {
    closeDailyMessage: () => void,
    dailyMessage: DailyMessage | null | undefined,
    isDailyMessageClosed: boolean,
    isErrorDailyMessagePrintable: boolean,
    isErrorTables: boolean,
    isErrorTablesTimeRanges: boolean,
    isLoadingTables: boolean,
    onSelectedTablesChange: (selectedTablesIds: Array<number>) => void,
    onTablesStateChange: (tablesState: Array<{status: TableStatus, tableNum: number}>) => void,
    selectedTableIds: Array<string>,
    tablesState?: Array<TableState>,
    tablesTimeRanges?: Array<TimeRange>
}

export function useTables({
    browsingPeriod,
    mode,
    onModeChange,
    previousMode,
    queryData,
    reservation,
    reservationId,
}: UseTablesInterface): UseTablesReturn {
    let refetchTablesInterval: NodeJS.Timer;

    const [ isLoadingTables, setIsLoadingTables ] = useState<boolean>(true);
    const [ isPollingTables, setIsPollingTables ] = useState<boolean>(false);
    const [ tablesState, setTablesState ] = useState<Array<TableState> | undefined>();

    const [ tablesTimeRanges, setTablesTimeRanges ] = useState<Array<TimeRange> | undefined>();

    const [ dailyMessage, setDailyMessage ] = useState<DailyMessage | null | undefined>();
    const [ isDailyMessageClosed, setIsDailyMessageClosed ] = useState<boolean>(false);

    const selectedTableIds = (tablesState?.filter(({ status }) => status === TableStatus.SELECTED).map(({ id }) => id) ?? []).sort();

    const tablesQueryEnabled = !(mode === Mode.SEARCHING || mode === Mode.REPORT);

    const { data: dataTables, isError: isErrorTables, isFetching: isFetchingTables, refetch: refetchTables } = useQuery({
        enabled: tablesQueryEnabled,
        queryFn: () => tablesRepository.fetchTables({
            // load whole day from API, filter it later during UI rendering
            end: getDateFormatted({ date: queryData.start, hour: 24 }),
            start: getDateFormatted({ date: queryData.start, hour: 0 }),
        }),
        queryKey: [ "tables", mode, browsingPeriod ],
        refetchOnWindowFocus: false,
    });

    const { data: dataTablesTimeRanges, isError: isErrorTablesTimeRanges } = useQuery({
        enabled: mode !== Mode.DAILY_MESSAGE_EDIT,
        queryFn: () => tablesRepository.getTablesTimeRanges({
            excludedReservation: mode === Mode.RESERVATION_EDIT ? reservationId : undefined,
            mode,
            start: getDateFormatted({ date: queryData.start, hour: 9 }),
            tableIds: selectedTableIds,
        }),
        queryKey: [ "tablesTimeRanges", selectedTableIds, moment(queryData.start).startOf("day"), mode ],
        refetchInterval: 20000,
        refetchOnWindowFocus: false,
    });

    const { data: dataDailyMessage, isError: isErrorDailyMessage, isFetching: isFetchingDailyMessage } = useQuery({
        // enabled: isPrinting,
        enabled: true,
        queryFn: () => dailyMessageRepository.getDailyMessage(moment(queryData.start).format("YYYY-MM-DD")),
        queryKey: [ "dailyMessage", queryData.start ],
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (dataTables) {
            setTablesState(getTablesStateFromData(dataTables ?? []));
        }
    }, [ dataTables, queryData.start, queryData.end ]);

    useEffect(() => {
        if (tablesQueryEnabled) {
            refetchTables();
        }
    }, [ queryData.start, queryData.end ]);

    useEffect(() => {
        setIsDailyMessageClosed(false);
    }, [ moment(queryData.start).startOf("day").valueOf(), moment(queryData.end).startOf("day").valueOf() ]);

    useEffect(() => {
        if (tablesQueryEnabled && mode !== Mode.PRINTING) {
            refetchTablesInterval = setInterval(() => {
                setIsPollingTables(true);
                refetchTables();
            }, 30000);
        }

        return () => {
            clearInterval(refetchTablesInterval);
        };
    }, [ mode ]);

    useEffect(() => {
        if (isPollingTables) {
            return setIsPollingTables(false);
        }
        if (isFetchingTables) {
            return setIsLoadingTables(true);
        }
        setTimeout(() => {
            setIsLoadingTables(false);
        }, 1);
    }, [ isFetchingTables ]);

    useEffect(() => {
        if (dataTablesTimeRanges) {
            setTablesTimeRanges(dataTablesTimeRanges);
        }
    }, [ dataTablesTimeRanges ]);

    useEffect(() => {
        setDailyMessage(dataDailyMessage);
    }, [ dataDailyMessage ]);

    useEffect(() => {
        if (!selectedTableIds.length) {
            setTablesTimeRanges(undefined);
        }
    }, [ selectedTableIds.length ]);

    useEffect(() => {
        if (mode === Mode.PRINTING && ![ isFetchingTables, isLoadingTables, isErrorTables, isFetchingDailyMessage, isErrorDailyMessage ].some(state => !!state)) {
            window.print();
        }

        window.addEventListener("afterprint", () => onPrintFinish());

        return () => window.removeEventListener("afterprint", () => onPrintFinish());
    }, [ mode, isLoadingTables, isFetchingTables, isErrorTables ]);

    function closeDailyMessage(): void {
        setIsDailyMessageClosed(true);
    }

    function getTableStateReservations(reservations: Array<Reservation>): Array<Reservation> {
        switch (mode) {
            case Mode.PRINTING:
                return getReservationsSortedByStartDate(reservations ?? []).slice(0, 3);

            default:
                return reservations ?? [];
        }
    }

    function getTableStateStatus(number: number, reservations: Array<Reservation> | undefined): TableStatus {
        const actualTableState: TableState | undefined = tablesState?.find(({ tableNum }) => tableNum === number);

        switch (mode) {
            case Mode.DAILY_MESSAGE_EDIT:
            case Mode.RESERVATION_BROWSING:
                if (
                    (reservations ?? []).find((reservation: Reservation) => new Date(reservation.start) < new Date(queryData.end) && new Date(reservation.end) > new Date(queryData.start))
                    && browsingPeriod === ReservationBrowsingPeriod.SELECTED_HOUR
                ) {
                    return TableStatus.BOOKED;
                } else {
                    return TableStatus.AVAILABLE;
                }

            case Mode.RESERVATION_PREVIEW:
            case Mode.RESERVATION_PREVIEW_FROM_SEARCH:
                if (reservation?.tables.map(table => table.number).includes(number)) {
                    return TableStatus.SELECTED;
                }
                return TableStatus.AVAILABLE;

            case Mode.PRINTING:
            case Mode.REPORT:
                return TableStatus.AVAILABLE;

            default:
                if (actualTableState?.status === TableStatus.SELECTED) {
                    return TableStatus.SELECTED;
                } else {
                    return TableStatus.AVAILABLE;
                }
        }
    }

    function getTablesStateFromData(dataTables: Array<Table>): Array<TableState> {
        return (dataTables ?? []).map(({ id, number, reservations }) => {
            return {
                id,
                reservations: getTableStateReservations(reservations ?? []),
                status: getTableStateStatus(number, reservations),
                tableNum: number,
            };
        });
    }

    function onSelectedTablesChange(selectedTables: Array<number>): void {
        setTablesState(prevState => prevState?.map(table => {
            const actualStatus = table.status;
            if (!selectedTables.length) {
                return { ...table, status: actualStatus === TableStatus.SELECTED ? TableStatus.AVAILABLE : actualStatus };
            }
            const newStatus = table.status === TableStatus.SELECTED ? TableStatus.AVAILABLE : table.status;
            return { ...table, status: selectedTables.includes(table.tableNum) ? TableStatus.SELECTED : newStatus };
        },
        ));
    }

    function onTablesStateChange(newTablesState: Array<{ status: TableStatus, tableNum: number }>) {
        setTablesState(tablesState?.map(table => {
            const updatedTable = newTablesState.find(({ tableNum }) => table?.tableNum === tableNum);
            if (updatedTable) {
                return { ...table, ...updatedTable };
            }
            return table;
        }));
    }

    function onPrintFinish(): void {
        switch (previousMode) {
            case Mode.RESERVATION_BROWSING:
                return onModeChange(previousMode);

            case Mode.SEARCHING:
            case Mode.REPORT:
                onTablesStateChange(tablesState?.map(tableState => ({ ...tableState, reservations: [], status: TableStatus.AVAILABLE })) ?? []);
                return onModeChange(previousMode);

            default:
                return onModeChange(Mode.RESERVATION_BROWSING);
        }
    }

    return {
        closeDailyMessage,
        dailyMessage,
        isDailyMessageClosed,
        isErrorDailyMessagePrintable: isErrorDailyMessage,
        isErrorTables,
        isErrorTablesTimeRanges,
        isLoadingTables,
        onSelectedTablesChange,
        onTablesStateChange,
        selectedTableIds,
        tablesState,
        tablesTimeRanges,
    };
}